import { saveInfo, getInfo, refreshCurrentRole } from './CacheStorage';
import {
  auth,
  axiosInstance,
  getParams,
  printLog,
} from './BaseApi';
import { cleanCaches } from 'src/utils/cache-data';
import { singleUpload, multiUpload } from './FormData';
const isDebug = process.env.REACT_APP_DEBUG === 'true';

const requestMap = {};

/**
 * Sends a POST request to the specified API endpoint.
 *
 * @param {string} endpoint - The URL or path of the API endpoint (e.g., 'api/demo/assign').
 * @param {Object|FormData} postBody - The body of the POST request.
 *   - Use a plain object for JSON.
 *   - Use FormData for file uploads or `multipart/form-data`.
 * @param {Object} [queryParams={}] - An object representing query parameters to append to the URL (e.g., { lang: 'vi' } → ?lang=vi).
 * @param {Object} [options={}] - Optional configuration.
 * @param {boolean} [options.isFormData=false] - If true, sets the request to use `multipart/form-data`.
 * @param {Object} [options.headers={}] - Additional headers to include in the request.
 * 
 * @returns {Promise<any>} A Promise that resolves with the parsed response or error object.
 */
export const postRequest = async (
  endpoint,
  postBody = {},
  queryParams = {},
  options = {}
) => {
  if (requestMap[endpoint]) {
    if (isDebug) {
      console.warn(`[WARN] Duplicate request blocked for endpoint: ${endpoint}`);
    }
    return;
  }

  requestMap[endpoint] = true;

  const isFormData = options.isFormData || false;

  // Default headers
  const extraHeaders = options.headers || {};
  const headerParams = isFormData
    ? { 'Content-Type': 'multipart/form-data', ...extraHeaders }
    : { ...extraHeaders };

  const config = getParams(queryParams, headerParams);

  try {
    const response = await axiosInstance.post(endpoint, postBody, config);
    printLog('POST', endpoint, config, response);
    return parseSuccess('POST', endpoint, response);
  } catch (error) {
    return parseError('POST', endpoint, error);
  } finally {
    requestMap[endpoint] = false;
  }
};

// Common function for handling POST requests
export const postData = async (endpoint, queryParams = {}, postBody = {}) => {
  if (requestMap[endpoint]) {
    if(isDebug){
      console.warn(`[WARN] Duplicate request blocked for endpoint: ${endpoint}`);
    }
    return;
  }
  requestMap[endpoint] = true;  // Set the flag to indicate a request is in progress
  
  try {
    const config = getParams(queryParams);
    const response = await axiosInstance.post(endpoint, postBody, config);
    printLog('POST', endpoint, config, response);
    return parseSuccess('POST', endpoint, response);
  } catch (error) {
    // throw error;
    return parseError('POST', endpoint, error);
  } finally {
    requestMap[endpoint] = false; // Reset the flag for this endpoint
  }
};

// Common function for handling GET requests
export const getData = async (endpoint, queryParams = {}) => {
  try {
    const config = getParams(queryParams);
    const response = await axiosInstance.get(endpoint, config);
    printLog('GET', endpoint, config, response);
    return parseSuccess('GET', endpoint, response);
  } catch (error) {
    // throw error;
    return parseError('GET', endpoint, error);
  }
};

// Common functoin for handling UPLOAD request data to server
export const postFormData = async (
  endpoint,
  formData = {},
  queryParams = {},
) => {
  if (requestMap[endpoint]) {
    if(isDebug){
      console.warn(`[WARN] Duplicate request blocked for endpoint: ${endpoint}`);
    }
    return;
  }
  requestMap[endpoint] = true;

  const headerParams = {
    'Content-Type': 'multipart/form-data',
  };
  const config = getParams(queryParams, headerParams);
  try {
    const response = await axiosInstance.post(endpoint, formData, config);
    printLog('POST', endpoint, config, response);
    return parseSuccess('POST', endpoint, response);
  } catch (error) {
    // throw error;
    return parseError('POST', endpoint, error);
  } finally {
    requestMap[endpoint] = false;
  }
};

export const startAuth = async (queryParams = {}) => {
  const response = await auth.accessToken(queryParams);
  return response;
};

export const stopAuth = async () => {
  cleanCaches();
  await auth.logout();
};

export const isauthed = () => {
  return auth.authenticated();
};

export const getRoles = () => {
  return auth.userRoles();
};

export const refreshRole = (newRole) => {
  return refreshCurrentRole(newRole);
};

function parseSuccess(method, endpoint, response) {
  // if(isDebug){
  //   console.log(`${method} > ${endpoint}`, response);
  // }
  const error = { code: 503, msg: 'Opps', note: null };
  if (response) {
    const status = response.status;
    if (200 === status) {
      let json = response.data;
      if (json.success) {
        if (json.data) return { result: json.data };
      }
      // Handle error from return api
      const arrays = json.data;
      if (arrays) {
        const length = arrays.length;
        if (0 < length) {
          error.code = arrays[0];
        }
        if (1 < length) {
          error.msg = `[${error.code}] ${arrays[1]}`; //`${arrays[1]} [${error.code}]`;
        }
        // if (2 < length) {
        //   error.note = `${arrays[2]}`;
        // }
      }
    }
  }
  return onErrorCode(error.code, error.msg); //undefined
}

const onErrorCode = (code, message) => {
  // if(isDebug){
  //   console.error('[ERROR]', code, message);
  // }
  const result = { code: code, message: message };
  // if (note) {
    // result.note = note;
  // }
  return { error: result };
};

function parseError(method, endpoint, error) {
  // if(isDebug){
  //   console.error(`${method} > ${endpoint}`, error);
  // }
  if (error.response) {
    // Extract status and message from error response if there's an error
    const status = error.response.status;
    const statusText = error.response.statusText;
    // const message = error.response.data.error;
    return onErrorCode(`${status}`, `${statusText}`);
  }
  return onErrorCode('405', 'Method Not Allowed');
}

export const getMe = async () => {
  let json = getInfo();
  if (json) {
    return json;
  }

  const { result } = await getData('api/getMyInfo');
  if (result) {
    json = await saveInfo(result);
  }
  return json;
};

// values is formik.values
export const createFormData = (values) => {
  const formData = new FormData();

  // Multi images
  const oldArrays = values.defaultImgs; // Get from: formik.setFieldValue("defaultImgs", assetCopy);
  const newArrays = values.images; // Get from images-uploader.js
  if (newArrays || oldArrays) {
    multiUpload(formData, newArrays, oldArrays);
  }
  // Single upload
  const oldImage = values.defaultImg; // Get from: formik.setFieldValue("defaultImg", assetCopy);
  const newImage = values.image; // Get from image-uploader.js
  if (newImage || oldImage) {
    singleUpload(formData, newImage, oldImage);
  }
  // Save images to define folder
  const saveFolder = values.saveToFolder;
  if(saveFolder){
    formData.append('folder', saveFolder);
  }

  // Log formData entries
  // if(isDebug){
  //   for (let [key, value] of formData.entries()) {
  //     console.log(`${key}:`, value);
  //   }
  // }

  return formData;
};

// function multiUpload(formData, newArrays, oldArrays) {
//   if (newArrays) {
//     // newArrays.forEach((image, index) => {
//     //   formData.append(`images[${index}]`, image.file);
//     // });

//     // Add to body
//     newArrays.forEach((image) => {
//       if (image.file) {
//         formData.append('images', image.file);
//       }
//     });
//   }

//   // Handle the case where oldArrays might be null or an empty array
//   // const nonMatchingItems = (!newArrays || newArrays.length === 0) ? [] : oldArrays.filter(itemO =>
//   //   !newArrays.some(itemN => itemN.id === itemO.id)
//   // );

//   // Non matching item ids
//   const nonMatchingItems =
//     !newArrays || newArrays.length === 0 || !oldArrays || oldArrays.length === 0
//       ? []
//       : oldArrays
//           .filter((itemO) => !newArrays.some((itemN) => itemN.id === itemO.id))
//           .map((item) => item.id);

//   // Add ids need remove
//   if (nonMatchingItems.length > 0) {
//     formData.append('remove', nonMatchingItems);
//   }
// }

// function singleUpload(formData, newImage, oldImage) {
//   if (newImage) {
//     // Add to body
//     formData.append('image', newImage);
//   }

//   // Add id need remove
//   if (newImage && oldImage) {
//     formData.append('remove', oldImage.id);
//   }
// }

/* 
 * @example
 * // Params (key - value) POST
 * await postRequest('api/<name>', {}, { myId: 13 });
 * 
 * @example
 * // JSON POST
 * await postRequest('api/<name>', { myId: 13, values: [1, 2, 3] });
 * 
 * @example
 * // FormData POST (e.g., file upload)
 * const formData = new FormData();
 * formData.append('myId', 13);
 * formData.append('file', selectedFile);
 * await postRequest('api/upload', formData, {}, { isFormData: true });
 * 
 * @example
 * // With query and custom header
 * await postRequest(
 *   'api/data',
 *   { name: 'Lorenm' },
 *   { lang: 'vi' },
 *   { headers: { 'X-Custom-Token': 'abc123' } }
 * );
 */
